/**
 * FilterButton.tsx — 多选下拉筛选（移植自原型 filterbutton.jsx）
 *
 * checkbox 多选 / radio 单选 + 可搜索 + 应用/清除；列表三件套的「筛选」部分。
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '~/lib/icons';

export interface FilterOption {
  value: string;
  label: string;
  hint?: string;
  count?: number;
}

interface FilterButtonProps {
  label: string;
  options?: Array<FilterOption | string>;
  value?: string[];
  onChange?: (value: string[]) => void;
  multi?: boolean;
  searchable?: boolean;
  width?: number;
  align?: 'left' | 'right';
  emptyLabel?: string;
}

export function FilterButton({
  label,
  options = [],
  value = [],
  onChange,
  multi = true,
  searchable = false,
  width = 220,
  align = 'left',
  emptyLabel,
}: FilterButtonProps) {
  const norm = useMemo<FilterOption[]>(
    () => options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o)),
    [options],
  );
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{
    top: number | null;
    bottom: number | null;
    left: number;
    width: number;
    maxH: number;
  } | null>(null);
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState<string[]>(value);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: 打开时同步外部 value 到草稿
  useEffect(() => {
    setDraft(value);
  }, [value, open]);

  const apply = (v: string[]) => {
    setOpen(false);
    if ((v || []).join(',') !== (value || []).join(',')) onChange?.(v);
  };
  const clear = () => {
    setDraft([]);
    onChange?.([]);
    setOpen(false);
  };
  const toggle = (val: string) => {
    if (multi) setDraft((d) => (d.includes(val) ? d.filter((x) => x !== val) : [...d, val]));
    else {
      onChange?.([val]);
      setOpen(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: 监听需在 open/draft 变化时重绑
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const margin = 8;
      let left = align === 'right' ? r.right - width : r.left;
      if (left + width > window.innerWidth - margin) left = window.innerWidth - width - margin;
      if (left < margin) left = margin;
      const maxH = 360;
      const spaceBelow = window.innerHeight - margin - (r.bottom + 4);
      const spaceAbove = r.top - margin - 4;
      // 下方空间不足以放下常规弹层且上方更宽 → 上翻：用 bottom 锚定 trigger 顶边（弹层底贴按钮上沿），
      // 弹层实际高度自适应、不留空隙。旧实现固定按 maxH 上移 top → 矮视口下短弹层被推到面包屑处（定位 bug）。
      if (spaceBelow < 220 && spaceAbove > spaceBelow) {
        setPos({
          top: null,
          bottom: window.innerHeight - r.top + 4,
          left,
          width,
          maxH: Math.min(maxH, spaceAbove),
        });
      } else {
        setPos({
          top: r.bottom + 4,
          bottom: null,
          left,
          width,
          maxH: Math.min(maxH, Math.max(160, spaceBelow)),
        });
      }
    };
    update();
    const onDoc = (e: MouseEvent) => {
      if (
        popRef.current?.contains(e.target as Node) ||
        triggerRef.current?.contains(e.target as Node)
      )
        return;
      apply(draft);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, draft, width, align]);

  const filtered = norm.filter((o) => !q || o.label.toLowerCase().includes(q.toLowerCase()));
  const count = (value || []).length;
  const displayLabel =
    count === 0
      ? label
      : count === 1
        ? `${label} · ${norm.find((o) => o.value === value[0])?.label || value[0]}`
        : `${label} · ${count}`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="btn"
        onClick={() => setOpen((o) => !o)}
        style={
          count > 0
            ? {
                background: 'var(--accent-soft)',
                borderColor: 'var(--accent)',
                color: 'var(--accent-fg)',
              }
            : undefined
        }
      >
        <span>{displayLabel}</span>
        {count > 0 && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              clear();
            }}
            title="清除"
            style={{ display: 'inline-flex', padding: 1, borderRadius: 999 }}
          >
            <Icons.IconX size={11} />
          </span>
        )}
        {count === 0 && <Icons.IconChevDown size={11} />}
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={popRef}
            style={{
              position: 'fixed',
              ...(pos.top != null ? { top: pos.top } : { bottom: pos.bottom ?? undefined }),
              left: pos.left,
              width: pos.width,
              background: 'var(--bg-elev)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-md)',
              zIndex: 200,
              animation: 'fade-in 0.1s ease',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: pos.maxH,
            }}
          >
            {searchable && (
              <div
                style={{
                  padding: 6,
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Icons.IconSearch size={12} style={{ color: 'var(--fg-muted)', marginLeft: 4 }} />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="搜索…"
                  style={{
                    flex: 1,
                    border: 0,
                    outline: 0,
                    background: 'transparent',
                    color: 'var(--fg)',
                    font: 'inherit',
                    fontSize: 12,
                  }}
                />
              </div>
            )}
            <div style={{ flex: 1, overflow: 'auto', padding: 4 }}>
              {filtered.length === 0 && (
                <div style={{ padding: '12px 10px', color: 'var(--fg-faint)', fontSize: 12 }}>
                  {emptyLabel || '无结果'}
                </div>
              )}
              {filtered.map((o) => {
                const on = draft.includes(o.value);
                return (
                  <div
                    key={o.value}
                    onClick={() => toggle(o.value)}
                    style={{
                      padding: '6px 8px',
                      borderRadius: 5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12.5,
                      cursor: 'pointer',
                      background: on ? 'var(--bg-subtle)' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--bg-subtle)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = on ? 'var(--bg-subtle)' : 'transparent';
                    }}
                  >
                    {multi ? (
                      <span
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: 3,
                          border: on
                            ? '1.5px solid var(--accent)'
                            : '1.5px solid var(--border-strong)',
                          background: on ? 'var(--accent)' : 'transparent',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          flexShrink: 0,
                        }}
                      >
                        {on && <Icons.IconCheck size={9} />}
                      </span>
                    ) : (
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 999,
                          border: on
                            ? '4px solid var(--accent)'
                            : '1.5px solid var(--border-strong)',
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {o.label}
                    </span>
                    {o.count !== undefined && (
                      <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
                        {o.count}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {multi && (
              <div
                style={{
                  borderTop: '1px solid var(--border)',
                  padding: 6,
                  display: 'flex',
                  gap: 6,
                }}
              >
                <button type="button" className="btn btn-sm" style={{ flex: 1 }} onClick={clear}>
                  清除
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-accent"
                  style={{ flex: 1 }}
                  onClick={() => apply(draft)}
                >
                  <Icons.IconCheck size={11} />
                  应用
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
